import classNames from 'classnames'
import { useField } from 'formik'
import type { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { useRef, type JSX } from 'react'

import { Checkbox, CheckboxProps } from '@ui/components/Checkbox'

import type { DeepNonNullable } from '../types/DeepNonNullable'
import type { DeepRequired } from '../types/DeepRequired'
import type { TypedFieldProps } from '../types/TypedFieldProps'
import type { ValidateField } from '../types/ValidateField'

type FieldConfig = Omit<FormikFieldConfig, 'name'>
interface FormFieldProps extends FieldConfig {
  onChange?(value: boolean): void
  children?: React.ReactNode
  size?: CheckboxProps['size']
}

export function RawFormCheckBoxField<FormValues>(
  props: FormFieldProps & TypedFieldProps<FormValues>
): JSX.Element {
  const { name = '', onChange, label, ...inputProps } = props

  const fieldRef = useRef<HTMLDivElement>(null)

  const [field, meta, helper] = useField<boolean>({
    name,
    type: 'checkbox',
    validate: inputProps.validate,
  })

  const touched = meta.touched
  const error = touched ? meta.error : undefined

  const handleCheckBox = async (value: boolean) => {
    if (onChange) {
      onChange(value)
    } else {
      await helper.setValue(value)
      await helper.setTouched(true)
    }
  }

  return (
    <div ref={fieldRef}>
      <div className={classNames('control')}>
        <Checkbox checked={field.value} error={error} onChange={handleCheckBox} {...inputProps}>
          {label || props.children}
        </Checkbox>
      </div>
    </div>
  )
}

export function createFormCheckBoxField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ) {
    return <RawFormCheckBoxField {...props} />
  }
}
